/**
 * Customer-facing error mapping.
 *
 * Customers should never read raw Supabase, SQL, Laravel, or vendor messages.
 * Backend handlers return { code, message } when they can; this helper maps
 * those codes (and known legacy raw-message patterns) into a {title, description}
 * pair suitable for vue-sonner toasts or inline banners.
 *
 * Usage:
 *   import { extractFriendlyError } from '@/utils/errorHandler'
 *   const { title, description } = extractFriendlyError(err)
 *   toast.error(title, { description })
 *
 * If you only need a single string (no description split), use friendlyErrorString().
 */

import type { AxiosError } from 'axios'
import { userFacingError, isTechnicalErrorMessage } from './userFacingError'

export type FriendlyErrorCode =
    | 'BOOKING_CONFLICT'
    | 'BOOKING_FAILED'
    | 'PERMISSION_DENIED'
    | 'SYSTEM_ERROR'
    | 'AI_UNAVAILABLE'
    | 'FILE_TOO_LARGE'
    | 'INVALID_FILE_TYPE'
    | 'UPLOAD_FAILED'
    | 'VALIDATION_ERROR'
    | 'SPACES_UNAVAILABLE'
    | 'DATE_UNAVAILABLE'
    | 'PRODUCTION_NOT_SCHEDULED'

export interface FriendlyError {
    code: FriendlyErrorCode
    title: string
    description: string
}

const FRIENDLY: Record<FriendlyErrorCode, { title: string; description: string }> = {
    BOOKING_CONFLICT: {
        title: 'This studio time is no longer available',
        description: 'Another booking was confirmed during your selected time. Please choose a different slot to continue.',
    },
    BOOKING_FAILED: {
        title: "We couldn't complete your booking",
        description: 'Please try again in a moment.',
    },
    PERMISSION_DENIED: {
        title: "You don't have access to this page",
        description: 'Contact your organization admin if you believe you should have permission.',
    },
    SYSTEM_ERROR: {
        title: "We couldn't complete your request",
        description: 'Please try again in a moment.',
    },
    AI_UNAVAILABLE: {
        title: 'Content enhancement is temporarily unavailable',
        description: 'Please try again in a moment.',
    },
    FILE_TOO_LARGE: {
        title: 'File is too large',
        description: 'Files must be under 50 MB.',
    },
    INVALID_FILE_TYPE: {
        title: 'Unsupported file type',
        description: 'Use a PDF, image, PowerPoint, Word, or Excel file.',
    },
    UPLOAD_FAILED: {
        title: 'Upload failed',
        description: 'Please check your file type and size, then try again.',
    },
    VALIDATION_ERROR: {
        title: 'Please review your information',
        description: 'Some fields need attention before you can continue.',
    },
    SPACES_UNAVAILABLE: {
        title: 'Studio booking is temporarily unavailable',
        description: "We're unable to process studio bookings right now. Please try again shortly or contact support if the issue continues.",
    },
    DATE_UNAVAILABLE: {
        title: 'This date is no longer available',
        description: 'Please select a different date.',
    },
    PRODUCTION_NOT_SCHEDULED: {
        title: "We couldn't schedule this production",
        description: 'Please review your booking details and try again. If the issue continues, contact support.',
    },
}

/**
 * Extract a friendly error from any thrown value (Axios error, plain Error,
 * unknown). Reads `response.data.code` first, then pattern-matches legacy
 * raw messages (transitional — backends are being refactored to always
 * return a code), then falls back to HTTP status, then a generic message.
 */
export function extractFriendlyError(error: unknown): FriendlyError {
    const ax = error as AxiosError<{ code?: string; message?: string; error?: string }>
    const data = ax?.response?.data
    const status = ax?.response?.status

    // 1. Honor explicit backend code when present.
    const code = data?.code
    if (code && isKnownCode(code)) {
        return { code, ...FRIENDLY[code] }
    }

    // 2. Pattern-match legacy raw messages (Supabase / SQL / OVP API).
    // These are removed once every backend caller returns `code`. Until
    // then, this catches the worst leaks so customers don't see them.
    const raw = (data?.message ?? data?.error ?? '').toString()

    if (/Overlaps with/i.test(raw))       return wrap('BOOKING_CONFLICT')
    if (/OVP API/i.test(raw))             return wrap('PRODUCTION_NOT_SCHEDULED')
    if (/SQLSTATE|doesn'?t have a default value|Class .*? not found|Undefined (?:array key|index|variable)/i.test(raw)) {
        return wrap('SYSTEM_ERROR')
    }
    // Catch-all for any framework path leak like "participants.0.email" or
    // "The X field is required." that slipped past validation overrides.
    if (/^The [a-z_.0-9]+\b.*(?:required|field)\b/i.test(raw)) {
        return wrap('VALIDATION_ERROR')
    }

    // 3. HTTP-status fallback.
    if (status === 401 || status === 403) return wrap('PERMISSION_DENIED')
    // For a 422 with a customer-friendly message the backend already wrote
    // (e.g. our overridden 'File must be under 50 MB' / 'Use a PDF, image,
    // PowerPoint, Word, or Excel file.'), surface that text instead of the
    // generic "Some fields need attention" — which was actively misleading
    // when the real cause was a server upload cap. We only fall through to
    // the generic when the raw message looks like a framework path
    // ('attribute.0.field is required') that we already swallow above.
    if (status === 422 && raw && raw.length > 0 && raw.length < 240 && !isTechnicalErrorMessage(raw)) {
        return { code: 'VALIDATION_ERROR', title: FRIENDLY.VALIDATION_ERROR.title, description: raw }
    }
    if (status === 422)                   return wrap('VALIDATION_ERROR')
    if (status === 413)                   return wrap('FILE_TOO_LARGE')
    if (status === 503)                   return wrap('SPACES_UNAVAILABLE')
    if (status && status >= 500)          return wrap('SYSTEM_ERROR')

    // 4. Last-resort generic message.
    return wrap('SYSTEM_ERROR')
}

/**
 * Single-string convenience for call sites that don't need the title/description split.
 * Format: "Title — Description"
 */
export function friendlyErrorString(error: unknown): string {
    const { title, description } = extractFriendlyError(error)
    return `${title} — ${description}`
}

/**
 * Return a known friendly error by code. Use when you already know the code
 * (e.g. client-side enforced limits like file size) and just want the copy.
 */
export function friendly(code: FriendlyErrorCode): FriendlyError {
    return wrap(code)
}

function wrap(code: FriendlyErrorCode): FriendlyError {
    return { code, ...FRIENDLY[code] }
}

function isKnownCode(code: string): code is FriendlyErrorCode {
    return Object.prototype.hasOwnProperty.call(FRIENDLY, code)
}

interface ApiErrorResponse {
    message?: string
    error?: string
}

/**
 * Legacy single-string extractor — preserves useful backend instructions and
 * replaces technical diagnostics. Prefer extractFriendlyError for new
 * customer-facing surfaces; kept for existing callers (e.g. useAssetLibrary).
 */
export function extractErrorMessage(error: AxiosError<ApiErrorResponse>): string {
    const fallback = "We couldn't complete that. Please try again. If it keeps happening, contact support."
    if (error.response?.data?.message) return userFacingError(error.response.data.message, fallback)
    // Several endpoints (Covert checklist 409s, upstream proxies) put the
    // server's explanation under `error` instead of `message` (SM-9057).
    if (error.response?.data?.error) return userFacingError(error.response.data.error, fallback)
    return userFacingError(error.message, fallback)
}

/**
 * Build a structured context object from an error for Sentry/console.
 * Safe to pass as the second argument to `console.error`. Self-contained so it
 * works regardless of the extractErrorMessage signature on this branch.
 */
export function serializeError(err: unknown): Record<string, unknown> {
    if (err == null || typeof err !== 'object') {
        return { message: String(err), raw: err }
    }

    const anyErr = err as {
        message?: string
        name?: string
        code?: string | number
        status?: number
        response?: { status?: number; data?: unknown }
    }

    return {
        message: anyErr?.message,
        name: anyErr?.name,
        // Filestack errors expose `code`; some expose `status`.
        code: anyErr?.code,
        status: anyErr?.status ?? anyErr?.response?.status,
        responseData: anyErr?.response?.data,
    }
}
